import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import PageHeader from "../../components/common/PageHeader";
import FoodPhotosItem from "./FoodPhotosItem";
import { useXhr } from "hooks/useXhr";
import { useFoodPhotosContext } from "hooks/useFoodPhotosContext";
import { useFoodFactContext } from "hooks/useFoodFactContext";
import { Button, Spinner } from "react-bootstrap";
import 'react-quill/dist/quill.snow.css';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    rectSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function FoodPhotos() {
    //States
    const [item, setItem] = useState({
        photo: null,
        alt: null,
        title: null,
        titleEng: null,
        titleFra: null,
        titleGer: null,
        titleDut: null,
        text1: null,
        textEng: null,
        textFra: null,
        textGer: null,
        textDut: null,
        position: null,
    });
    const [status, setStatus] = useState(false);
    const [isUpdate, setIsUpdate] = useState(null);
    const [isFoodFact, setIsFoodFact] = useState(null);
    const [lang, setLang] = useState(null);
    const [langFoodFact, setLangFoodFact] = useState(null);
    const [isDelete, setIsDelete] = useState(null);
    const [isModal, setIsModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({
        field: null,
        note: null
    });
    const [sortableIds, setSortableIds] = useState([]);

    //Hooks
    const { customXhr } = useXhr()
    const { foodPhotos, dispatch } = useFoodPhotosContext();
    const { foodFact, dispatch: foodFactDispatch } = useFoodFactContext();

    const requiredFields = ['alt', 'title', 'text1', 'position'];
    const requiredFieldsForFoodFact = ['title', 'text1'];
    const maxTextLength = 200;

    const handleOnChange = (e) => {
        const { id, value } = e.target;

        setItem(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleOnUpdateChange = (e) => {
        const { id, value } = e.target;

        setIsUpdate(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleOnFoodFactChange = (e) => {
        const { id, value } = e.target;

        setIsFoodFact(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const generateUniqueFileName = (file) => {
        const timestamp = Date.now();
        const randomNumber = Math.floor(Math.random() * 10000);
        const extension = file.name.split('.').pop();

        return `${timestamp}_${randomNumber}.${extension}`;
    };

    const handlePhotoInput = (e) => {
        const file = e.target.files[0];
        const id = e.target.id;

        const newFileName = generateUniqueFileName(file);
        const newFile = new File([file], newFileName, { type: file.type });

        if (isUpdate) {
            setIsUpdate(prevState => ({
                ...prevState,
                [id]: newFile
            }));
        } else {
            setItem(prevState => ({
                ...prevState,
                [id]: newFile
            }));
        }
    };

    const handleDelete = async (id) => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'food_photos');
        formData.append('action', 'delete');
        formData.append('id', id);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setStatus(!status);
            setIsDelete(null);
            setIsLoading(false);
        };
    };

    const handleUpdate = async () => {
        for (const field of requiredFields) {
            if (!isUpdate[field] || isUpdate[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'food_photos');
        formData.append('action', 'update');
        formData.append('id', isUpdate.id);
        formData.append('photo', isUpdate.photo);
        formData.append('alt', isUpdate.alt);
        formData.append('title', isUpdate.title);
        formData.append('titleEng', isUpdate.titleEng);
        formData.append('titleFra', isUpdate.titleFra);
        formData.append('titleGer', isUpdate.titleGer);
        formData.append('titleDut', isUpdate.titleDut);
        formData.append('text1', isUpdate.text1);
        formData.append('textEng', isUpdate.textEng);
        formData.append('textFra', isUpdate.textFra);
        formData.append('textGer', isUpdate.textGer);
        formData.append('textDut', isUpdate.textDut);
        formData.append('position', isUpdate.position);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setIsUpdate(null);
            setLang(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const handleFoodFact = async () => {
        for (const field of requiredFieldsForFoodFact) {
            if (!isFoodFact[field] || isFoodFact[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'food_fact');
        formData.append('action', 'update');
        formData.append('id', isFoodFact.id);
        formData.append('title', isFoodFact.title);
        formData.append('titleEng', isFoodFact.titleEng);
        formData.append('titleFra', isFoodFact.titleFra);
        formData.append('titleGer', isFoodFact.titleGer);
        formData.append('titleDut', isFoodFact.titleDut);
        formData.append('text1', isFoodFact.text1);
        formData.append('textEng1', isFoodFact.textEng1);
        formData.append('textFra1', isFoodFact.textFra1);
        formData.append('textGer1', isFoodFact.textGer1);
        formData.append('textDut1', isFoodFact.textDut1);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setIsFoodFact(null);
            setLangFoodFact(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const handleSubmit = async () => {
        for (const field of requiredFields) {
            if (!item[field] || item[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'food_photos');
        formData.append('action', 'insert');
        formData.append('photo', item.photo);
        formData.append('alt', item.alt);
        formData.append('title', item.title);
        formData.append('titleEng', item.titleEng);
        formData.append('titleFra', item.titleFra);
        formData.append('titleGer', item.titleGer);
        formData.append('titleDut', item.titleDut);
        formData.append('text1', item.text1);
        formData.append('textEng', item.textEng);
        formData.append('textFra', item.textFra);
        formData.append('textGer', item.textGer);
        formData.append('textDut', item.textDut);
        formData.append('position', item.position);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setItem({
                photo: null,
                alt: null,
                title: null,
                titleEng: null,
                titleFra: null,
                titleGer: null,
                titleDut: null,
                text1: null,
                textEng: null,
                textFra: null,
                textGer: null,
                textDut: null,
                position: null,
            });
            setIsModal(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const getFoodPhotos = async () => {
        const formData = new FormData();
        formData.append('module', 'food_photos');
        formData.append('action', 'list');

        const response = await customXhr(formData);

        if (response) {
            dispatch({ type: 'SET_FOOD_PHOTOS', payload: response });
            setItem(prevState => ({
                ...prevState,
                position: response.length > 0 ? response.length + 1 : 1
            }));
            getFoodFact();
        };
    };

    const getFoodFact = async () => {
        const formData = new FormData();
        formData.append('module', 'food_fact');
        formData.append('action', 'list');

        const response = await customXhr(formData);

        if (response) {
            foodFactDispatch({ type: 'SET_FOOD_FACT', payload: response[0] });
        };
    };

    useEffect(() => {
        getFoodPhotos();
    }, [status]);

    useEffect(() => {
        if (foodPhotos && foodPhotos.length > 0) {
            setSortableIds(foodPhotos.map(item => item.id.toString()));
        }
    }, [foodPhotos]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setSortableIds((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newOrder = arrayMove(items, oldIndex, newIndex);
                updateOrderOnBackend(newOrder);
                return newOrder;
            });
        }
    };

    const updateOrderOnBackend = async (newOrder) => {
        const formData = new FormData();
        formData.append('module', 'food_photos');
        formData.append('action', 'sort');
        // Sıralamayı id ve yeni position ile gönderiyoruz
        // newOrder: ["3", "1", "2", ...]
        newOrder.forEach((id, index) => {
            formData.append(`order[${index}][id]`, id);
            formData.append(`order[${index}][position]`, index + 1);
        });

        await customXhr(formData);
    };

    const lengthError = (field) => {
        if (isUpdate && isUpdate[field] && maxTextLength - isUpdate[field].length < 20) {
            return <span className="text-danger">
                {`${maxTextLength - (isUpdate && isUpdate[field] ? isUpdate[field].length : 0)} harf hakkınız kaldı!`}
            </span>;
        } else {
            return "";
        }
    };

    return (
        <div className="container-xxl">
            <PageHeader headerTitle="Gıda Sektörünün Reis'i" renderRight={() => {
                return <div className="col-auto d-flex w-sm-100">
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsFoodFact(foodFact); setLangFoodFact("tur"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/turkey.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Türkçe İçerik
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsFoodFact(foodFact); setLangFoodFact("eng"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/united-kingdom.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        İngilizce İçerik
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsFoodFact(foodFact); setLangFoodFact("fra"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/france.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Fransızca İçerik
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsFoodFact(foodFact); setLangFoodFact("ger"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/germany.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Almanca İçerik
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsFoodFact(foodFact); setLangFoodFact("dut"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/netherlands.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Felemenkçe İçerik
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100" onClick={() => { setIsModal(true) }}>
                        <i className="icofont-plus-circle me-2 fs-6"></i>
                        Yeni Öğe
                    </button>
                </div>
            }} />
            <div className="row align-items-center">
                <div className="col-lg-12 col-md-12 flex-column">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={sortableIds}
                            strategy={rectSortingStrategy}
                        >
                            <div className="row g-3 gy-5 py-3 row-deck">
                                {sortableIds.map((id) => {
                                    const d = foodPhotos.find(item => item.id.toString() === id);
                                    if (!d) return null;
                                    return (
                                        <SortableItem key={d.id} id={d.id.toString()}>
                                            <FoodPhotosItem
                                                id={d.id}
                                                photoName={d.photoName}
                                                alt={d.alt}
                                                title={d.title}
                                                text1={d.text1}
                                                onClickEdit={() => { setIsUpdate(d); setLang("tur") }}
                                                onClickEditEng={() => { setIsUpdate(d); setLang("eng") }}
                                                onClickEditFra={() => { setIsUpdate(d); setLang("fra") }}
                                                onClickEditGer={() => { setIsUpdate(d); setLang("ger") }}
                                                onClickEditDut={() => { setIsUpdate(d); setLang("dut") }}
                                                onClickDelete={() => { setIsDelete(d); }}
                                            />
                                        </SortableItem>
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
            <Modal
                show={isModal}
                onHide={() => {
                    setIsModal(null);
                    setItem(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Yeni Öğe</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="title" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="title"
                            value={item && item.title}
                            onChange={handleOnChange}
                            style={{ borderColor: `${error.field === "title" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "title" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="text1" className="form-label">
                            {"Yazı "}
                            {lengthError("text1")}
                        </label>
                        <textarea
                            className="form-control"
                            id="text1"
                            rows="5"
                            value={item && item.text1}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "text1" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "text1" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="alt" className="form-label">
                            {"Seo Kelimeler "}
                            <span className="text-danger">
                                (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                            </span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="alt"
                            value={item && item.alt}
                            onChange={handleOnChange}
                            style={{ borderColor: `${error.field === "alt" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "alt" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="photo" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photo"
                            onChange={handlePhotoInput}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "tur"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="title" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="title"
                            value={isUpdate && isUpdate.title}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "title" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "title" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="text1" className="form-label">
                            {"Yazı "}
                            {lengthError("text1")}
                        </label>
                        <textarea
                            className="form-control"
                            id="text1"
                            rows="5"
                            value={isUpdate && isUpdate.text1}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "text1" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "text1" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="alt" className="form-label">
                            {"Seo Kelimeler "}
                            <span className="text-danger">
                                (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                            </span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="alt"
                            value={isUpdate && isUpdate.alt}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "alt" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "alt" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="photo" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            id="photo"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label" style={{ color: "red" }}>
                            {"Bu alanın geçerli görsel öğe boyutu: 1220x800"}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "eng"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İngilizce Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleEng" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleEng"
                            value={isUpdate && isUpdate.titleEng}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleEng" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleEng" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textEng" className="form-label">
                            {"Yazı "}
                            {lengthError("textEng")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textEng"
                            rows="5"
                            value={isUpdate && isUpdate.textEng}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textEng" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textEng" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "fra"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Fransızca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleFra" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleFra"
                            value={isUpdate && isUpdate.titleFra}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleFra" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleFra" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textFra" className="form-label">
                            {"Yazı "}
                            {lengthError("textFra")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textFra"
                            rows="5"
                            value={isUpdate && isUpdate.textFra}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textFra" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textFra" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "ger"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Almanca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleGer" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleGer"
                            value={isUpdate && isUpdate.titleGer}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleGer" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleGer" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textGer" className="form-label">
                            {"Yazı "}
                            {lengthError("textGer")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textGer"
                            rows="5"
                            value={isUpdate && isUpdate.textGer}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textGer" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textGer" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "dut"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Felemenkçe Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleDut" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleDut"
                            value={isUpdate && isUpdate.titleDut}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleDut" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleDut" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textDut" className="form-label">
                            {"Yazı "}
                            {lengthError("textDut")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textDut"
                            rows="5"
                            value={isUpdate && isUpdate.textDut}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textDut" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textDut" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langFoodFact === "tur"}
                onHide={() => {
                    setIsFoodFact(null);
                    setLangFoodFact(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="title"
                                value={isFoodFact && isFoodFact.title}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "title" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "title" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="text1" className="form-label">Yazı</label>
                            <textarea
                                className="form-control"
                                id="text1"
                                rows="5"
                                value={isFoodFact && isFoodFact.text1}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "text1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "text1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleFoodFact()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langFoodFact === "eng"}
                onHide={() => {
                    setIsFoodFact(null);
                    setLangFoodFact(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İngilizce İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleEng" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleEng"
                                value={isFoodFact && isFoodFact.titleEng}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "titleEng" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleEng" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textEng1" className="form-label">Yazı</label>
                            <textarea
                                className="form-control"
                                id="textEng1"
                                rows="5"
                                value={isFoodFact && isFoodFact.textEng1}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "textEng1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textEng1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleFoodFact()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langFoodFact === "fra"}
                onHide={() => {
                    setIsFoodFact(null);
                    setLangFoodFact(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Fransızca İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleFra" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleFra"
                                value={isFoodFact && isFoodFact.titleFra}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "titleFra" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleFra" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textFra1" className="form-label">Yazı</label>
                            <textarea
                                className="form-control"
                                id="textFra1"
                                rows="5"
                                value={isFoodFact && isFoodFact.textFra1}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "textFra1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textFra1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleFoodFact()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langFoodFact === "ger"}
                onHide={() => {
                    setIsFoodFact(null);
                    setLangFoodFact(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Almanca İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleGer" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleGer"
                                value={isFoodFact && isFoodFact.titleGer}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "titleGer" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleGer" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textGer1" className="form-label">Yazı</label>
                            <textarea
                                className="form-control"
                                id="textGer1"
                                rows="5"
                                value={isFoodFact && isFoodFact.textGer1}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "textGer1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textGer1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleFoodFact()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langFoodFact === "dut"}
                onHide={() => {
                    setIsFoodFact(null);
                    setLangFoodFact(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Felemenkçe İçerik Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleDut" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleDut"
                                value={isFoodFact && isFoodFact.titleDut}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "titleDut" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleDut" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textDut1" className="form-label">Yazı</label>
                            <textarea
                                className="form-control"
                                id="textDut1"
                                rows="5"
                                value={isFoodFact && isFoodFact.textDut1}
                                onChange={handleOnFoodFactChange}
                                style={{ borderColor: `${error.field === "textDut1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textDut1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleFoodFact()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                centered
                show={isDelete}
                onHide={() => {
                    setIsDelete(null);
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Uyarı</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="form-label">
                            Bu öğeyi silmek istediğinize emin misiniz?
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        className="me-1 text-white"
                        onClick={() => handleDelete(isDelete.id)}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Siliniyor...
                                </> :
                                <>
                                    <i className="icofont-ui-delete"></i>
                                    &ensp;
                                    Sil
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

function SortableItem({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab"
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="col-md-6 col-sm-6"
        >
            {children}
        </div>
    );
}

export default FoodPhotos;